<i18n>
{
  "en": {
    "version": {
      "uploadForm": {
        "title": "Version Upload",
        "secretKey": "Secret Key",
        "file": "Select file"
      }
    }
  }
}
</i18n>

<template>
  <div class="content-block">
    <footer class="footer" v-if="version && canReadVersions">
      {{ `${version.appVersion}.${appInfo.feBuildNum}/${version.buildNumber}` }}
      {{ `[db:${version.dbInfo.schema}.${version.dbInfo.fwInfo.schemaVersion},` }}
      <a href="javascript:;" @click="onVersionClick">{{ 'vrs: ' }}{{
        version.modelsVersion? version.modelsVersion.versionNumber: 'no version'
      }}</a>
      {{ ']' }}
      <router-link v-if="isShowErrorLicenseLink" :class="getClassLicense()" :to="{ name: 'license', params: { id: defaultLocation.code } }">{{ userLicensesRelevant.statusDescription }}</router-link>
    </footer>
    <footer class="footer" v-else>{{ copyright }}</footer>
    <dx-popup :visible.sync="popupVisible"
      :drag-enabled="false"
      :show-close-button="true"
      :show-colon-after-label="false"
      :show-title="true"
      :title="$t('version.uploadForm.title')"
      :width="500"
      height="auto"
      @hiding="onPopupHiding"
    >
      <div id="footer-upload-form-wrapper">
        <dx-form
          label-location="top"
          :form-data="uploadData"
          :col-count="1"
        >
          <dx-group-item>
            <dx-simple-item data-field="secretKey">
              <dx-label :text="$t('version.uploadForm.secretKey')" />
            </dx-simple-item>
            <dx-simple-item>
              <!-- eslint-disable vue/no-unused-vars -->
              <div slot-scope="_">
                <dx-file-uploader
                  :select-button-text="$t('version.uploadForm.file')"
                  label-text=""
                  accept="*"
                  upload-mode="useForm"
                  ref="uploader"
                  @value-changed="onFileSelected"
                />
              </div>
              <!-- eslint-enable -->
              <dx-label :text="''" />
            </dx-simple-item>
          </dx-group-item>
          <dx-group-item>
            <!-- eslint-disable vue/no-unused-vars -->
            <div slot-scope="_" class="footer-upload-form-buttons">
              <aims2-button
                type="default"
                :text="$t('general.upload')"
                :disabled="uploading"
                :busy="uploading"
                @click="onUpload"
              />
              <aims2-button
                type="danger"
                :text="$t('general.cancel')"
                @click="onCancel"
              />
            </div>
            <!-- eslint-enable -->
          </dx-group-item>

        </dx-form>
      </div>
    </dx-popup>
  </div>
</template>

<script>
import appInfo from "../app-info";

import { DxPopup } from 'devextreme-vue/popup';
import {
  DxForm,
  DxSimpleItem,
  DxGroupItem,
  DxLabel
} from 'devextreme-vue/form';
import { DxFileUploader } from 'devextreme-vue/file-uploader';
import aims2Button from "@/components/active-button.vue";

import { notifier } from '@/shared/lib/notifier/index';

import { getBuildVersion, versionUpload, getUserLocationsPage, getUserLicensesRelevant } from "@/services/api";
import { AclHandler } from "@/services/acl-handler";
import { DataBacked } from "@/mixins/dataBackedMixin";

export default {
  mixins: [ DataBacked ],
  components: {
    DxPopup, DxForm, DxSimpleItem, DxGroupItem, DxLabel,
    DxFileUploader, aims2Button
  },
  data() {
    return {
      isCloudMode: this.$store.getters.isCloudMode,
      appInfo,
      version: { dbInfo: { fwInfo: {}}, modelsVersion: {}},
      userLicensesRelevant: null,
      copyright: `${appInfo.title}.${appInfo.feBuildNum}/XX [db:xxxx]`,
      defaultLocation: null,
      popupVisible: false,
      uploadData: {
        secretKey: null,
        version: null,
      },
      uploading: false
    }
  },
  computed: {
    appUser() {
      return this.$store.getters.user;
    },
    aclUser() {
      return new AclHandler(this.appUser, this.isCloudMode);
    },
    isShowErrorLicenseLink() {
      return this.userLicensesRelevant && 
        ['ERROR', 'WARNING'].includes(this.userLicensesRelevant.status);
    },
    canReadVersions() {
      return this.aclUser.hasGrantForOperation('MNU_VERSIONS', 'READ') || false;
    }
  },
  methods: {
    async loadVersion() {
      try {
        const locations = await getUserLocationsPage({
            filterJson: ['code', '=', 'DEFAULT'],
            page: '1,1',
        });
        this.defaultLocation = locations.pageRows.length > 0 ? locations.pageRows[0] : null;
        this.$store.commit("setDefaultLocation", this.defaultLocation);

        const version = await getBuildVersion();
        // eslint-disable-next-line
        // console.debug("footer::VERSION", version);
        if (version) {
          this.version = version;
          this.copyright = `${version.appVersion}.${appInfo.feBuildNum}/${version.buildNumber}
          [db:${version.dbInfo.schema}.${version.dbInfo.fwInfo.schemaVersion}, vrs: ${version.modelsVersion ? version.modelsVersion.versionNumber : ''}]
          `;
          window.localStorage[`${document.location.pathname}-copyright`] = this.copyright;
        }
        
        const license = await getUserLicensesRelevant();
        this.userLicensesRelevant = license;
        this.$store.commit("setUserLicensesRelevant", license);

        if (this.isShowErrorLicenseLink)
          notifier.showError(license.statusDescription);
      } catch (err) {
        // eslint-disable-next-line
        console.error("footer::loadVersion", err);
      }
    },
    onVersionClick(e) {
      e.preventDefault();
      this.popupVisible = true;
    },
    async onUpload() {
      try {
        const formData = new FormData();
        formData.append("secretKey", this.uploadData.secretKey);
        formData.append("version", this.uploadData.version);
        await versionUpload(formData);
        notifier.showSuccess(this.$t('general.uploadSuccess'));
        this.popupVisible = false;
        this.loadVersion();
      } catch (err) {
        this.onDataError(err);
      }
    },
    onCancel() {
      this.popupVisible = false;
    },
    onPopupHiding() {
      this.uploadData.secretKey = null;
      this.uploadData.version = null;
      this.$refs.uploader.instance.reset();
    },
    onFileSelected({ value }) {
      this.uploadData.version = value[0];
    },
    getClassLicense() {
      const className = 'license-';
      if (this.userLicensesRelevant) {
        if (this.userLicensesRelevant.status === 'ERROR') {
          return className + 'error';
        }
        if (this.userLicensesRelevant.status === 'WARNING')  {
          return className + 'warning';
        }
      }
    }
  },
  created() {
    this.loadVersion();
  }
}
</script>

<style lang="scss">
@import "../themes/generated/variables.base.scss";

.footer {
  display: block;
  text-align: center;
  color: rgba($base-text-color, alpha($base-text-color) * 0.7);
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding: 8px 0;

  // padding-top: 20px;
  // padding-bottom: 24px;
  // margin-left: 1rem;
}

#footer-upload-form-wrapper {
  .footer-upload-form-buttons {
    text-align: center;
    .dx-button {
      width: 140px;
      margin: 0 8px;
    }
  }
  .dx-item {
    margin-bottom: 10px;
  }
  .dx-invalid-message-content {
    padding: 0 10px !important;
  }
}

.license-error {
  color: #ff0000 !important;
}

.license-warning {
  color: #ffa500 !important;
}
</style>
